import { isValidElement } from 'react';
import type { ReactNode, ReactElement } from 'react';

/**
 * Extract the base-level (unprefixed) Tailwind utilities from a class string.
 *
 * Variant-prefixed utilities (`md:hidden`, `hover:flex`) are skipped on purpose:
 * Tailwind emits them into a later layer than plain utilities, so they already
 * override the defaults this component applies and must not suppress them for
 * every breakpoint.
 *
 * The `:` check is a blunt instrument: it also drops base-level utilities whose
 * arbitrary value happens to contain a colon (`text-[color:red]`). None of the
 * display/font-weight/text-transform/tracking families this module checks
 * against are ever written that way, so it is a non-issue for every current
 * caller — but keep it in mind if this helper is ever reused for a family that
 * takes `property:value` arbitrary values.
 *
 * @param className - Space-separated Tailwind class string.
 * @returns The set of base-level utility names, with `!important` markers stripped.
 */
export const getBaseUtilities = (className: string): Set<string> =>
  new Set(
    className
      .split(/\s+/)
      .filter((token) => token.length > 0 && !token.includes(':'))
      .map((token) => token.replace(/^!/, '')),
  );

/**
 * Check whether a class string contains any of the given base-level utilities.
 *
 * Matching is on whole utilities, so `overflow-hidden` does not count as `hidden`.
 *
 * @param className - Space-separated Tailwind class string.
 * @param utilities - Utility names to look for.
 * @returns true when at least one base-level utility from the set is present.
 */
export const hasBaseUtility = (className: string, utilities: ReadonlySet<string>): boolean => {
  for (const utility of getBaseUtilities(className)) {
    if (utilities.has(utility)) return true;
  }
  return false;
};

/**
 * Check whether a class string contains a base-level utility starting with a prefix.
 *
 * Useful for open-ended utility families such as `tracking-*`, where the value
 * part cannot be enumerated (`tracking-[0.2em]`).
 *
 * @param className - Space-separated Tailwind class string.
 * @param prefix - Utility prefix to look for (e.g. `'tracking-'`).
 * @returns true when at least one base-level utility starts with the prefix.
 */
export const hasBaseUtilityPrefix = (className: string, prefix: string): boolean => {
  for (const utility of getBaseUtilities(className)) {
    if (utility.startsWith(prefix)) return true;
  }
  return false;
};

/**
 * Get the display name or function/class name for a React element.
 *
 * Unwraps common wrappers (such as React.memo) to retrieve
 * the underlying component's displayName or name in a safe way.
 *
 * @param node - The React node to extract the component display name from.
 * @returns The component's displayName or name, or null if it cannot be determined
 *   (for DOM elements, fragments, non-element children, or unknown shapes).
 */
export const getComponentDisplayName = (node: ReactNode): string | null => {
  if (!isValidElement(node)) return null;

  const element: ReactElement = node;
  let elementType: unknown = element.type;

  // Unwrap React.memo where inner component may be under .type
  if (typeof elementType === 'object' && elementType !== null) {
    elementType = (elementType as { type?: unknown }).type ?? elementType;
  }

  // If the type is a function (function component / class), prefer displayName then name
  if (typeof elementType === 'function') {
    const fn = elementType as { displayName?: string; name?: string };
    return fn.displayName ?? fn.name ?? null;
  }

  // If the type is an object (some HOC metadata), try to read displayName/name
  if (typeof elementType === 'object' && elementType !== null) {
    const obj = elementType as { displayName?: string; name?: string };
    return obj.displayName ?? obj.name ?? null;
  }

  // For DOM string types or unknown shapes return null
  return null;
};

/**
 * Type-guard: checks whether a React node is an element whose underlying
 * component has a given displayName or function/class name.
 *
 * @param node - React node to inspect.
 * @param displayName - Expected displayName / name (e.g. 'Highlighter').
 * @returns true if node is a React element and its component name matches displayName.
 */
export const isElementWithDisplayName = (
  node: ReactNode,
  displayName: string,
): node is ReactElement => {
  if (!isValidElement(node)) return false;
  const name = getComponentDisplayName(node);
  return name === displayName;
};
